import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DataTableWithFilters,
  type DataTableFilter,
} from "@/components/ui/DataTableWithFilter";
import { DEFAULT_PAGE_SIZE, type DateRangeFilter } from "@/helpers";
import { useToast } from "@/components/ui";
import {
  meetingsService,
  type Meeting,
  type MeetingListRequest,
} from "@/services/api/meetings";
import {
  callingAgentsService,
  type CallingAgentDropdownOption,
} from "@/services/api/callingAgents";

type AppointmentRow = {
  id: string;
  subject: string;
  lead: string;
  lead_email: string;
  lead_phone: string;
  ai_agent: string;
  meeting_date: string;
  start_time: string;
  end_time: string;
  __raw: Meeting;
};

type AppointmentFilterState = {
  aiAgentIds: string[];
  dateRange?: DateRangeFilter;
};

type AppointmentRequestFilter = NonNullable<MeetingListRequest["filter"]>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const getString = (value: unknown): string | undefined => {
  if (typeof value !== "string" && typeof value !== "number") return undefined;

  const normalized = String(value).trim();
  return normalized || undefined;
};

const getStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    const item = getString(value);
    return item ? [item] : [];
  }

  return value.reduce<string[]>((items, item) => {
    const normalized = getString(item);
    return normalized ? [...items, normalized] : items;
  }, []);
};

const readString = (
  source: Record<string, unknown> | null | undefined,
  key: string,
): string | undefined => (source ? getString(source[key]) : undefined);

const formatDateTime = (value?: string | null) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDate = (value?: string | null) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const getAppointmentId = (meeting: Meeting, index: number) =>
  getString(meeting.meetingId) ??
  getString(meeting.meetingUID) ??
  `APT-${String(index + 1).padStart(3, "0")}`;

const getLeadAttendee = (meeting: Meeting) => meeting.attendees?.lead?.[0];

const getLeadName = (meeting: Meeting) => {
  const lead = isRecord(meeting.lead) ? meeting.lead : undefined;
  const attendee = getLeadAttendee(meeting);

  return (
    readString(lead, "fullName") ??
    readString(lead, "full_name") ??
    getString(attendee?.name) ??
    "-"
  );
};

const getLeadEmail = (meeting: Meeting) => {
  const lead = isRecord(meeting.lead) ? meeting.lead : undefined;
  const attendee = getLeadAttendee(meeting);

  return readString(lead, "emailAddress") ?? getString(attendee?.email) ?? "-";
};

const getLeadPhone = (meeting: Meeting) => {
  const lead = isRecord(meeting.lead) ? meeting.lead : undefined;
  return readString(lead, "phoneNumber") ?? "-";
};

const getAiAgentName = (meeting: Meeting) =>
  getString(meeting.aiAgentData?.display_name) ??
  getString(meeting.aiAgentId) ??
  "-";

const getDropdownOptions = (response: any): CallingAgentDropdownOption[] => {
  const options = response?.attributes ?? response?.data ?? [];

  return Array.isArray(options) ? options : [];
};

const getResponseList = (response: any): Meeting[] => {
  const list = response?.attributes?.data;
  return Array.isArray(list) ? list : [];
};

const getResponseTotal = (response: any, fallback: number) => {
  const total =
    response?.attributes?.pagination?.total ?? response?.attributes?.total;
  const parsed = Number(total);

  return Number.isFinite(parsed) ? parsed : fallback;
};

const buildMeetingListPayload = (
  pageNumber: number,
  pageSize: number,
  search: string,
  filters: AppointmentFilterState,
) => {
  const filter: AppointmentRequestFilter = {};
  const normalizedSearch = search.trim();

  if (filters.dateRange) {
    filter.dateRangeType = "dateRange";
    filter.startDate = filters.dateRange.dateFrom;
    filter.endDate = filters.dateRange.dateTo;
  }

  return {
    pageNumber,
    pageSize,
    ...(filters.aiAgentIds.length ? { aiAgentIds: filters.aiAgentIds } : {}),
    ...(normalizedSearch ? { search: normalizedSearch } : {}),
    ...(Object.keys(filter).length ? { filter } : {}),
  };
};

const renderSubject = (value: string, row: AppointmentRow) => (
  <div className="min-w-56 max-w-xs whitespace-normal">
    <p className="font-medium text-gray-900">{value}</p>
    {row.__raw.description && (
      <p className="mt-1 line-clamp-2 text-xs text-gray-500">
        {row.__raw.description}
      </p>
    )}
  </div>
);

const renderLead = (_: string, row: AppointmentRow) => (
  <div className="min-w-48 whitespace-normal">
    <p className="font-medium text-gray-900">{row.lead}</p>
    <p className="text-xs text-gray-500">{row.lead_email}</p>
    <p className="text-xs text-gray-500">{row.lead_phone}</p>
  </div>
);

const renderAiAgent = (value: string, row: AppointmentRow) => (
  <div className="min-w-36 whitespace-normal">
    <p className="font-medium text-gray-800">{value}</p>
    {row.__raw.aiAgentData?.type && (
      <p className="text-xs capitalize text-gray-500">
        {row.__raw.aiAgentData.type}
      </p>
    )}
  </div>
);

const renderDateTime = (value: string) => (
  <span className="whitespace-nowrap text-gray-700">{value}</span>
);

export default function AllAppointments() {
  const { error: toastError } = useToast();
  const [appointments, setAppointments] = useState<Meeting[]>([]);
  const [agentOptions, setAgentOptions] = useState<
    CallingAgentDropdownOption[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAgentsLoading, setIsAgentsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [activeFilters, setActiveFilters] = useState<AppointmentFilterState>(
    { aiAgentIds: [] },
  );
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [totalItems, setTotalItems] = useState(0);

  const handleSearch = useCallback((query: string) => {
    setSearch(query);
    setPage(1);
  }, []);

  const handleItemsPerPageChange = useCallback((nextItemsPerPage: number) => {
    setItemsPerPage(nextItemsPerPage);
    setPage(1);
  }, []);

  const handleFilterChange = useCallback((filters: Record<string, unknown>) => {
    setActiveFilters((prev) => {
      const next: AppointmentFilterState = { ...prev };

      if ("aiAgentIds" in filters) {
        next.aiAgentIds = getStringArray(filters.aiAgentIds);
      }

      if ("dateRange" in filters) {
        next.dateRange = isRecord(filters.dateRange)
          ? (filters.dateRange as unknown as DateRangeFilter)
          : undefined;
      }

      return next;
    });
    setPage(1);
  }, []);

  const fetchAppointments = useCallback(async () => {
    setIsLoading(true);

    try {
      const response = await meetingsService.listing(
        buildMeetingListPayload(page, itemsPerPage, search, activeFilters),
      );
      const list = getResponseList(response);

      setAppointments(list);
      setTotalItems(getResponseTotal(response, list.length));
    } catch (error: any) {
      toastError(error?.message || "Failed to load appointments");
      setAppointments([]);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
    }
  }, [activeFilters, itemsPerPage, page, search, toastError]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  useEffect(() => {
    let isMounted = true;

    const fetchAgents = async () => {
      setIsAgentsLoading(true);

      try {
        const response = await callingAgentsService.dropdown();
        const options = getDropdownOptions(response);

        if (isMounted) {
          setAgentOptions(options);
        }
      } catch (error: any) {
        if (isMounted) {
          toastError(error?.message || "Failed to load agent options");
        }
      } finally {
        if (isMounted) {
          setIsAgentsLoading(false);
        }
      }
    };

    fetchAgents();

    return () => {
      isMounted = false;
    };
  }, [toastError]);

  const tableFilters = useMemo<DataTableFilter[]>(
    () => [
      {
        key: "aiAgentIds",
        label: "Agent",
        type: "multi-select",
        placeholder: isAgentsLoading ? "Loading agents..." : "All Agents",
        options: agentOptions,
      },
    ],
    [agentOptions, isAgentsLoading],
  );

  const columns = [
    { label: "Subject", key: "subject", render: renderSubject },
    { label: "Lead", key: "lead", render: renderLead },
    { label: "AI-Agent", key: "ai_agent", render: renderAiAgent },
    { label: "Meeting Date", key: "meeting_date", render: renderDateTime },
    { label: "Start Time", key: "start_time", render: renderDateTime },
    { label: "End Time", key: "end_time", render: renderDateTime },
  ];

  const rows = useMemo<AppointmentRow[]>(
    () =>
      appointments.map((appointment, index) => ({
        id: getAppointmentId(appointment, index),
        subject: getString(appointment.subject) ?? "Appointment",
        lead: getLeadName(appointment),
        lead_email: getLeadEmail(appointment),
        lead_phone: getLeadPhone(appointment),
        ai_agent: getAiAgentName(appointment),
        meeting_date: formatDate(
          getString(appointment.meetingDate) ?? getString(appointment.startTime),
        ),
        start_time: formatDateTime(appointment.startTime),
        end_time: formatDateTime(appointment.endTime),
        __raw: appointment,
      })),
    [appointments],
  );

  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
            Appointments Management
          </h1>
          <p className="text-xs sm:text-sm text-gray-600">
            View and manage all Appointments.
          </p>
        </div>
      </div>
      <DataTableWithFilters
        title="Appointments"
        columns={columns}
        data={rows}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        currentPage={page}
        onPageChange={setPage}
        onItemsPerPageChange={handleItemsPerPageChange}
        filters={tableFilters}
        showFilters
        showDateRangeFilter
        showExport={false}
        loading={isLoading}
        onSearch={handleSearch}
        onFilterChange={handleFilterChange}
        searchDebounceMs={500}
        searchPlaceholder="Search appointments"
      />
    </div>
  );
}
